import * as React from 'react';
import PropTypes from 'prop-types';
import { styled } from "@mui/material/styles"
import Stepper from "@mui/material/Stepper"
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import LayersIcon from '@mui/icons-material/Layers';
import GradingIcon from '@mui/icons-material/Grading';
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector"

const ArtosConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 10,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#4338CA',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#1F477D',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderColor: '#eaeaf0',
        borderTopWidth: 3,
        borderRadius: 1,
        ...theme.applyStyles('dark', {
            borderColor: theme.palette.grey[800],
        }),
    },
}));

const ArtosStepIconRoot = styled('div')(({ theme }) => ({
    color: '#eaeaf0',
    display: 'flex',
    height: 24,
    alignItems: 'center',
    '& .ArtosStepIcon-completedIcon': {
        color: '#1F477D',
        zIndex: 1,
        // fontSize: 24,
        width: 24,
        height: 24,
    },
    '& .ArtosStepIcon-circle': {
        width: 20,
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
    },
    ...theme.applyStyles('dark', {
        color: theme.palette.grey[700],
    }),
    variants: [
        {
            props: ({ ownerState }) => ownerState.active,
            style: {
                color: '#1F477D',
            },
        },
    ],
}));

function ArtosStepIcon(props) {
    const { active, completed, className } = props;

    return (
        <ArtosStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (
                <CheckCircle className="ArtosStepIcon-completedIcon" />
            ) : (
                <div className="ArtosStepIcon-circle" />
            )}
        </ArtosStepIconRoot>
    )
}

ArtosStepIcon.propTypes = {
    /**
     * Whether this step is active.
     * @default false
     */
    active: PropTypes.bool,
    className: PropTypes.string,
    /**
     * Mark the step as completed. Is passed to child components.
     * @default false
     */
    completed: PropTypes.bool,
}

export default function HorizontalStepper({
    steps,
    activeStep,
}) {
    return (
        <Stepper alternativeLabel activeStep={activeStep} connector={<ArtosConnector />}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ArtosStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    )
}

/* ---------------------------------------------------------------------------------------------- */
/* Artoslib Horizontal Stepper                                                                    */
/* ---------------------------------------------------------------------------------------------- */

const ArtoslibConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`&.${stepConnectorClasses.active}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage:
            'linear-gradient( 95deg,rgb(0, 212, 255) 0%,rgba(70, 152, 250) 20%,rgb(139, 92, 246) 50%,rgba(85, 81, 185) 75%,rgb(31,71,125) 100%)',
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        [`& .${stepConnectorClasses.line}`]: {
            backgroundImage:
            'linear-gradient( 95deg,rgb(0, 212, 255) 0%,rgba(70, 152, 250) 20%,rgb(139, 92, 246) 50%,rgba(85, 81, 185) 75%,rgb(31,71,125) 100%)',
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: '#eaeaf0',
        borderRadius: 1,
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[400],
        }),
    },
}));
  
const ArtoslibStepIconRoot = styled('div')(({ theme }) => ({
    backgroundColor: '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    ...theme.applyStyles('dark', {
        backgroundColor: theme.palette.grey[300],
    }),
    variants: [
        {
            props: ({ ownerState }) => ownerState.active,
            style: {
                backgroundImage:
                'linear-gradient( 95deg,rgb(0, 212, 255) 0%,rgba(70, 152, 250) 20%,rgb(139, 92, 246) 50%,rgba(85, 81, 185) 75%,rgb(31,71,125) 100%)',
                boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
            },
        },
        {
            props: ({ ownerState }) => ownerState.completed,
            style: {
                backgroundImage:
                'linear-gradient( 95deg,rgb(0, 212, 255) 0%,rgba(70, 152, 250) 20%,rgb(139, 92, 246) 50%,rgba(85, 81, 185) 75%,rgb(31,71,125) 100%)',
            },
        },
    ],
}));

function ArtoslibStepIcon(props) {
    const { active, completed, className } = props;

    const icons = {
        1: <EditNoteIcon />,
        2: <InsertDriveFileIcon />,
        3: <LayersIcon />,
        4: <GradingIcon />,
    };

    return (
        <ArtoslibStepIconRoot ownerState={{ completed, active }} className={className}>
        {icons[String(props.icon)]}
        </ArtoslibStepIconRoot>
    );
}

ArtoslibStepIcon.propTypes = {
    /**
     * Whether this step is active.
     * @default false
     */
    active: PropTypes.bool,
    className: PropTypes.string,
    /**
     * Mark the step as completed. Is passed to child components.
     * @default false
     */
    completed: PropTypes.bool,
    /**
     * The label displayed in the step icon.
     */
    icon: PropTypes.node,
};

export function ArtoslibHorizontalStepper({
    steps,
    activeStep,
}) {
    return (
        <Stepper alternativeLabel activeStep={activeStep} connector={<ArtoslibConnector />}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={ArtoslibStepIcon}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    )
}
